// Auto DMs (the Automations module) — the web-app's client for the `/v1/.../automations`
// surface. Kept out of the page so the shapes and the calls live in one place. Not in
// `@api` (the extension does not speak this surface — it is admin-only, dev-only for now).
import { isCodedError } from '@api/errors';

import { apiGet, apiSend } from './transport';

export interface AutoStats {
  active: number;
  completed: number;
  stopped: number;
  failed: number;
  /** Runs whose send never came back, so nobody can say whether the member got it.
   *  Terminal — the owner clears one by adding the member to the sequence again. */
  unknown: number;
  sent: number;
}

export interface AutoStep {
  after: string;
  message: string;
}

export interface SenderInfo {
  external_id: string | null;
  name: string;
  connected: boolean;
  is_default: boolean;
}

export interface SenderOption {
  external_id: string;
  name: string;
  avatar_url: string | null;
  connected: boolean;
}

/** Whether a sequence can actually send. `can_enable` answers one question — do we hold
 *  the sender's credential — and there are four other ways to be switched on and silent,
 *  which is what this carries. Optional: it is additive on the server, so a build that
 *  predates it keeps working. */
export interface SequenceHealth {
  status: 'ok' | 'cannot_send' | 'degraded';
  reason:
    | 'no_credential'
    | 'credential_expired'
    | 'sender_not_staff'
    | 'no_roster_access'
    | 'rate_limited'
    | 'sends_unknown'
    | null;
  /** A sentence for a person. Switch on `reason`, never on this. */
  detail?: string | null;
}

export interface Automation {
  key: string;
  name: string;
  trigger: string;
  enabled: boolean;
  is_builtin: boolean;
  enabled_at: string | null;
  steps: AutoStep[];
  stop_on: string[];
  sender: SenderInfo;
  can_enable: boolean;
  stats: AutoStats;
  health?: SequenceHealth | null;
}

export interface AutomationList {
  items: Automation[];
  total: number;
  community: { id: number; slug: string; name: string };
  triggers: string[];
  senders: SenderOption[];
}

export interface NewAutomation {
  key: string;
  name: string;
  trigger: string;
  steps: AutoStep[];
  stop_on: string[];
  sender_external_id: string | null;
}

const base = (skoolId: number) => `/v1/communities/${skoolId}/automations`;

export function fetchAutomations(skoolId: number): Promise<AutomationList> {
  return apiGet<AutomationList>(base(skoolId));
}

export function enableAutomation(skoolId: number, key: string): Promise<Automation> {
  return apiSend<Automation>('POST', `${base(skoolId)}/${encodeURIComponent(key)}/enable`);
}

export function disableAutomation(skoolId: number, key: string): Promise<Automation> {
  return apiSend<Automation>('POST', `${base(skoolId)}/${encodeURIComponent(key)}/disable`);
}

export function createAutomation(skoolId: number, body: NewAutomation): Promise<Automation> {
  return apiSend<Automation>('POST', base(skoolId), body);
}

export function updateAutomation(
  skoolId: number,
  key: string,
  body: { name: string; steps: AutoStep[]; stop_on: string[] },
): Promise<Automation> {
  return apiSend<Automation>(
    'POST', `${base(skoolId)}/${encodeURIComponent(key)}/definition`, body);
}

export interface RecipientOption {
  external_id: string;
  name: string;
  avatar_url: string | null;
  handle: string | null;
}

export function fetchTestRecipients(
  skoolId: number,
  key: string,
  q: string,
): Promise<{ items: RecipientOption[] }> {
  return apiGet<{ items: RecipientOption[] }>(
    `${base(skoolId)}/${encodeURIComponent(key)}/test-recipients`,
    { q: q || undefined });
}

export interface TestSendResult {
  sent: boolean;
  recipient_name: string;
  message_id: string | null;
  preview: string;
}

export function sendTest(
  skoolId: number,
  key: string,
  recipientExternalId: string,
  stepIndex = 0,
): Promise<TestSendResult> {
  return apiSend<TestSendResult>(
    'POST', `${base(skoolId)}/${encodeURIComponent(key)}/test-send`,
    { recipient_external_id: recipientExternalId, step_index: stepIndex });
}

/** Structured delay: an integer + a unit, so the editor never takes a free-text time. */
export const DELAY_UNITS: { v: string; label: string }[] = [
  { v: 'm', label: 'minutes' },
  { v: 'h', label: 'hours' },
  { v: 'd', label: 'days' },
  { v: 'w', label: 'weeks' },
];

export function parseAfter(after: string): { n: number; unit: string } {
  const m = /^(\d+)\s*([mhdw])$/.exec((after || '').trim());
  return m ? { n: Number(m[1] ?? 0), unit: m[2] ?? 'm' } : { n: 0, unit: 'm' };
}

export function formatAfter(n: number, unit: string): string {
  return `${Math.max(0, Math.floor(n || 0))}${unit}`;
}

export function setSender(
  skoolId: number,
  key: string,
  senderExternalId: string | null,
): Promise<Automation> {
  return apiSend<Automation>('POST', `${base(skoolId)}/${encodeURIComponent(key)}/sender`, {
    sender_external_id: senderExternalId,
  });
}

// --- activity log + live queue -------------------------------------------------
export interface ActivityItem {
  member_external_id: string | null;
  member_name: string;
  avatar_url: string | null;
  step_index: number;
  status: string; // sending | sent | failed | unknown
  body: string | null;
  error: string | null;
  message_id: string | null;
  /** The DM thread this attempt posted into. On an `unknown` receipt it is the only
   *  handle that exists — no message id was ever stamped — so it is what the owner
   *  follows to find out whether the member got the message. */
  channel_id?: string | null;
  sent_at: string | null;
  source: string; // trigger | manual
}

export function fetchActivity(
  skoolId: number,
  key: string,
): Promise<{ items: ActivityItem[]; total: number }> {
  return apiGet<{ items: ActivityItem[]; total: number }>(
    `${base(skoolId)}/${encodeURIComponent(key)}/activity`);
}

export interface QueueItem {
  member_external_id: string | null;
  member_name: string;
  avatar_url: string | null;
  handle: string | null;
  step_index: number;
  steps_total: number;
  next_step_at: string | null;
  enrolled_at: string | null;
  paused_in_conversation: boolean;
  source: string; // trigger | manual
  /** Why this member has not had their next step yet, or null when nothing is in the
   *  way and it is simply not due. `scope` says whose problem it is: `sequence` (nobody
   *  here is getting anything) or `member` (this one person, this one step). `detail` is
   *  the server's sentence — shown when we have no better wording of our own. */
  waiting?: {
    scope: string;
    reason: string;
    detail?: string | null;
    since?: string | null;
  } | null;
  /** How many times this step has come back transient — the "N of 3" beside it. */
  send_attempts?: number;
}

/** A waiting reason in the owner's words. Keyed on the stable enum the send door and the
 *  tick both answer in (`db/send_reasons.py`); an enum we have no wording for falls back
 *  to the server's own sentence, so a reason added there reads plainly instead of
 *  disappearing. */
const WAITING_COPY: Record<string, string> = {
  not_a_sender: 'the sending account is no longer staff of this community',
  sender_not_connected: "the sender's Skool account is not connected",
  blocked_env: 'this environment does not send real messages',
  switched_off: 'sending is switched off',
  backed_off: 'Skool is throttling the sending account',
  box_backed_off: 'Skool is throttling us',
  over_budget: 'the sending account has sent as much as it may for now',
  credential_dead: "the sender's stored Skool login could not be read",
  auth_expired: "the sender's Skool session has expired",
  rate_limited: 'Skool is rate-limiting the sending account',
  transient: 'the last attempt never reached Skool',
  outcome_unknown: 'a send could not be confirmed, so this member is stopped',
  send_failed: 'Skool refused the message',
  no_recipient: 'this member has no Skool account to send to',
  already_reserved: 'a send for this step is already in flight',
  in_conversation: 'they are mid-conversation with the sender',
};

export function waitingText(w: { reason: string; detail?: string | null }): string {
  return WAITING_COPY[w.reason] ?? w.detail ?? w.reason.replace(/_/g, ' ');
}

export function fetchQueue(
  skoolId: number,
  key: string,
): Promise<{ items: QueueItem[]; total: number }> {
  return apiGet<{ items: QueueItem[]; total: number }>(
    `${base(skoolId)}/${encodeURIComponent(key)}/queue`);
}

/** Manually add a member to the sequence — a first trigger by hand (flagged `manual`).
 *  Returns the automation with refreshed counts. */
export function addEnrollment(
  skoolId: number,
  key: string,
  memberId: number,
): Promise<Automation> {
  return apiSend<Automation>(
    'POST', `${base(skoolId)}/${encodeURIComponent(key)}/enrollments`,
    { member_id: memberId });
}

/** Pull a member out of an in-flight sequence — returns the automation with fresh counts. */
export function removeEnrollment(
  skoolId: number,
  key: string,
  memberExternalId: string,
): Promise<Automation> {
  return apiSend<Automation>(
    'POST',
    `${base(skoolId)}/${encodeURIComponent(key)}/enrollments/` +
      `${encodeURIComponent(memberExternalId)}/remove`);
}

/** What is wrong, and what to do about it — two sentences, never one.
 *
 *  THE FIX IS A SEPARATE FIELD because the two are read differently: the first sentence
 *  is what the owner needs to believe the warning, the second is the only part they can
 *  act on, and a single run-on sentence buries the second half of it.
 *
 *  Keyed on the server's `reason` enum, NOT on its `detail` sentence: the enum is the
 *  contract and the sentence is written for an operator reading a terminal. An unknown
 *  reason falls back to that sentence with no fix, so a reason added on the server later
 *  reads plainly here instead of disappearing. */
export interface HealthCopy {
  what: string;
  fix: string;
}

const HEALTH_COPY: Record<string, HealthCopy> = {
  no_credential: {
    what: 'We have no connected Skool account to send as.',
    fix: "Connect the sender's Skool account in the Sideguide Helper.",
  },
  credential_expired: {
    what: "The sender's Skool session has expired.",
    fix: "Reconnect the sender's Skool account in the Sideguide Helper.",
  },
  sender_not_staff: {
    what: 'The sender is no longer an admin or moderator of this community.',
    fix: 'Pick another sender below, or make them staff again on Skool.',
  },
  no_roster_access: {
    what: 'We can no longer read your members page, so nobody reaches the trigger.',
    fix: 'Make the Sideguide account an admin of your community again.',
  },
  rate_limited: {
    what: 'Skool is rate-limiting the sending account.',
    fix: 'Nothing to do — sending resumes by itself.',
  },
  sends_unknown: {
    what: 'Some messages never came back, so we cannot say whether they arrived.',
    fix: 'Check those conversations, then remove and re-add the members to continue.',
  },
};

export function healthCopy(health: SequenceHealth): HealthCopy {
  const known = health.reason ? HEALTH_COPY[health.reason] : undefined;
  return known ?? { what: health.detail || 'Something is stopping this sequence.', fix: '' };
}

/** What a refused test send says to the person who pressed the button.
 *
 *  Keyed on the v1 error CODE, never on the message: the code is the contract and the
 *  message is prose the server may reword. A failure that carries no code at all (a dead
 *  socket, a proxy) falls back to its own message, which is the only thing that knows
 *  anything about it.
 *
 *  `send_outcome_unknown` is the one line here that must not read "try again": the
 *  message may already have gone, and pressing the button again would send a real person
 *  the same DM twice. */
const TEST_SEND_ERRORS: Record<string, string> = {
  send_outcome_unknown:
    'We could not confirm the message was delivered. Check the conversation before ' +
    'sending again — it may already have arrived.',
  sending_disabled: 'Sending is switched off right now, so nothing was sent.',
  send_budget_spent:
    'This account has sent as much as it may for now. Try again in a few minutes.',
  sender_rate_limited: 'Skool is rate-limiting this account. Try again in a few minutes.',
  rate_limited: 'That is a lot of test messages. Wait a moment and try again.',
  sender_not_allowed:
    'That Skool account is not an admin or moderator here, so this community cannot ' +
    'send as it.',
  sender_not_connected:
    "Connect the sender's Skool account in the Sideguide Helper before test-sending.",
  recipient_not_in_inbox:
    'That person is not in the sender’s Skool inbox, so there is no conversation to ' +
    'send into.',
  test_send_failed: 'Skool refused the message, so nothing was sent.',
  no_such_step: 'That message is no longer part of the sequence.',
  no_group: 'There is no shared community to open the conversation in.',
};

export function testSendError(err: unknown): string {
  if (isCodedError(err)) {
    return TEST_SEND_ERRORS[err.code] ?? err.message;
  }
  return err instanceof Error ? err.message : 'Could not send the test DM.';
}

/** Trigger presentation — a readable label and an icon name, keyed by the registry slug.
 *  An unknown (future) trigger falls back to its slug and a generic icon. */
export function triggerMeta(trigger: string): { label: string; icon: string } {
  return TRIGGER_META[trigger] ?? { label: trigger, icon: 'send' };
}

export function triggerLabel(trigger: string): string {
  return triggerMeta(trigger).label;
}

/** The moment delays are measured FROM, as a clause that follows "after"/"when" — e.g.
 *  "after they join", "after you add them". Includes the SUBJECT because it is not always
 *  the member: a manual trigger is the owner's own act. Delays are cumulative from the
 *  trigger, not from the previous message. */
export function triggerClause(trigger: string): string {
  return TRIGGER_CLAUSE[trigger] ?? 'it triggers';
}

/** The short label for a step with no delay (fires at the trigger itself) — "on join",
 *  "when added". The 0-delay twin of {@link triggerClause}. */
export function triggerOnset(trigger: string): string {
  return TRIGGER_ONSET[trigger] ?? 'right away';
}

const TRIGGER_CLAUSE: Record<string, string> = {
  'member.joined': 'they join',
  'member.cancelled': 'they cancel',
  'member.payment_failed': 'their payment fails',
  manual: 'you add them',
};

const TRIGGER_ONSET: Record<string, string> = {
  'member.joined': 'on join',
  'member.cancelled': 'on cancel',
  'member.payment_failed': 'on payment failure',
  manual: 'when added',
};

const TRIGGER_META: Record<string, { label: string; icon: string }> = {
  'member.joined': { label: 'When a new member joins', icon: 'user' },
  'member.cancelled': { label: 'When someone cancels their membership', icon: 'trend' },
  'member.payment_failed': { label: 'When a payment fails', icon: 'spark' },
  manual: { label: 'When adding a member to the sequence', icon: 'send' },
};

/** A slug from a name, for the `key` a custom automation needs. Lowercase, hyphenated,
 *  matching the server's `^[a-z0-9][a-z0-9-]*$`. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'automation';
}
